// Local-timezone date helpers. The Worker stores everything as ISO YYYY-MM-DD
// in the local-civil-day sense (i.e. "what date is it for the operator right
// now"), so the web app must compute YYYY-MM-DD keys from LOCAL components,
// not from toISOString() (which is UTC and can shift the day in either
// direction depending on the offset).

/** Format a Date as `YYYY-MM-DD` using local timezone components. */
export function formatLocalDateToYMD(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Parse `YYYY-MM-DD` into a local-midnight Date. `new Date('2026-05-26')` is
 * UTC midnight and shifts the day in negative-UTC zones — this helper avoids
 * that gotcha so date arithmetic / .toLocaleDateString() stays on the
 * operator's civil day.
 *
 * Throws on:
 *   - Non-matching format ("hello", "2026/05/26", "2026-5-26").
 *   - Calendar overflow ("2026-02-30", "2026-13-01") — verified by
 *     round-tripping the constructed Date's year/month/day.
 *
 * All callers feed Zod-validated YYYY-MM-DD strings (server responses) or
 * the output of `formatLocalDateToYMD` (always valid), so a throw here only
 * fires on programmer error, not on operator data.
 */
export function parseLocalDate(ymd: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) throw new Error(`Invalid ISO date: ${ymd}`);
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const date = new Date(y, mo - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== mo - 1 ||
    date.getDate() !== d
  ) {
    throw new Error(`Invalid ISO date: ${ymd}`);
  }
  return date;
}

/**
 * Add `n` days (may be negative) to a `YYYY-MM-DD` key, returning a new key.
 * Uses local-date components (`setDate` rolls month/year over and is DST-safe,
 * since it operates on civil days) rather than millisecond arithmetic — a
 * `±N × 86_400_000` offset lands on the wrong civil day across the two DST
 * transitions each year. Throws (via parseLocalDate) on a malformed input.
 */
export function addDaysYMD(ymd: string, n: number): string {
  const date = parseLocalDate(ymd);
  date.setDate(date.getDate() + n);
  return formatLocalDateToYMD(date);
}
